import PersonIcon from '@mui/icons-material/Person';
import { Box, Paper, Typography } from '@mui/material';
import gender from 'common/constants/gender';
import { normalizePhoneNumber } from 'common/utils';
import { ShowDefaultPartnerContact } from 'common/utils/featureFlags';
import { differenceInYears, format } from 'date-fns';
import DefaultContactChip from 'modules/PatientView/components/DefaultContactChip';
import PlaceholderEmailMarker from 'modules/PatientView/components/PartnerContacts/PlaceholderEmailMarker';
import partnerContactPropType from 'modules/PatientView/components/PartnerContacts/partnerContactPropType';
import PropTypes from 'prop-types';

import choosePartnerContactForOrder from '../utils/choosePartnerContactForOrder';

const InfoRow = ({ label, value, labelAdornment, valueAdornment }) => (
  <Box role='group' aria-label={label} sx={{ mb: 1 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      <Typography variant='caption' color='text.secondary' display='block'>
        {label}
      </Typography>
      {labelAdornment}
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 1 }}>
      <Typography variant='body2'>{value || '—'}</Typography>
      {value && valueAdornment}
    </Box>
  </Box>
);

InfoRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  labelAdornment: PropTypes.node,
  valueAdornment: PropTypes.node,
};

InfoRow.defaultProps = { value: null, labelAdornment: null, valueAdornment: null };

// Contact details are the ones this order's partner notifies the patient on; editing them stays
// on the patient profile.
const PatientPanel = ({ order, patient, patientExternalId }) => {
  const orderContact = choosePartnerContactForOrder({
    patient,
    partnerUuid: order.partner?.partnerUuid,
    isDefaultContactShown: ShowDefaultPartnerContact,
  });

  return (
    <Paper variant='outlined' sx={{ flex: 4, p: 2.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
        <PersonIcon color='primary' fontSize='small' />
        <Typography variant='subtitle2' fontWeight='bold'>
          Patient
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 3, mb: 1.5 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <InfoRow label='MPI' value={order.mpi} />
        </Box>
        {patientExternalId && (
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <InfoRow label='External ID' value={patientExternalId} />
          </Box>
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 1.5 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <InfoRow label='Name' value={patient?.patientName || order.patientName} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <InfoRow
            label='Date of Birth'
            value={
              patient?.dob
                ? `${format(new Date(patient.dob), 'MM/dd/yyyy')} (${differenceInYears(new Date(), new Date(patient.dob))} yrs)`
                : null
            }
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <InfoRow
            label='Gender'
            value={patient?.gender ? gender[patient.gender.toLowerCase()] : null}
          />
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 2 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <InfoRow
            label='Email'
            value={orderContact.email}
            labelAdornment={
              orderContact.isDefaultEmailShown && <DefaultContactChip contactKind='email' />
            }
            valueAdornment={orderContact.isPlaceholderEmailShown && <PlaceholderEmailMarker />}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <InfoRow
            label='Phone'
            value={normalizePhoneNumber(orderContact.phoneNumber)}
            labelAdornment={
              orderContact.isDefaultPhoneShown && <DefaultContactChip contactKind='phone' />
            }
          />
        </Box>
      </Box>
    </Paper>
  );
};

PatientPanel.propTypes = {
  order: PropTypes.shape({
    mpi: PropTypes.string,
    patientName: PropTypes.string,
    partner: PropTypes.shape({
      partnerName: PropTypes.string,
      partnerUuid: PropTypes.string,
    }),
  }).isRequired,
  patient: PropTypes.shape({
    patientName: PropTypes.string,
    dob: PropTypes.string,
    gender: PropTypes.string,
    email: PropTypes.string,
    mobilePhone: PropTypes.string,
    contactsFromPartners: PropTypes.arrayOf(partnerContactPropType),
  }),
  patientExternalId: PropTypes.string,
};

PatientPanel.defaultProps = {
  patient: null,
  patientExternalId: null,
};

export default PatientPanel;
